import { BRANDS, determineBrandFromURL } from "../helpers/brand";

/* SVG underlines are part of the WILDE branding. The appearance is done in SCSS, 
but it breaks when the underlined area is spread over multiple lines.
This function fixes this issue by adding a separate underline span to each line  */
export const svgUnderline = () => {
  const brand = determineBrandFromURL();

  /* Only needed for WILDE */
  if (brand === BRANDS.STAYCITY) {
    return;
  }

  /* Get all svg underlines on the page */
  const underlineAreas =
    document.querySelectorAll<HTMLElement>(".svg-underline");

  const svgUnderlineInit = (underlineAreas: NodeListOf<HTMLElement>) => {
    underlineAreas.forEach((underlineArea) => {
      const initFlag = underlineArea.getAttribute("js-svg-underline-init");
      if (initFlag && initFlag == "false") {
        return;
      }

      /* Get line height and height of underline span */
      const underlineTextStyles = window.getComputedStyle(underlineArea);
      const lineHeight = +underlineTextStyles
        .getPropertyValue("line-height")
        .slice(0, -2);
      const height = underlineArea.offsetHeight;

      /* height is same or less than line height === span is not over multiple lines */
      if (height <= lineHeight) {
        underlineArea.classList.add("svg-underline");
        underlineArea.classList.remove("display--ib");
        underlineArea.innerHTML = underlineArea.textContent!;
        return;
      }

      /* Get lines of text wrapped in individual spans */
      const lines = extractLinesFromTextNode(underlineArea.firstChild as Text);

      /* Remove underline class from original span and replace with display: inline-block */
      /* Without inline-block the height of the new span is different from line-height and causing issues */
      /* Add span-wrapped lines */
      if (lines) {
        underlineArea.classList.remove("svg-underline");
        underlineArea.classList.add("display--ib");
        underlineArea.innerHTML = lines.join(" ");
      }
    });
  };

  /* Fix for screen resize cutting off bottom of text. Readjust max-height for acitve accordions on resize  */
  ["load", "resize"].forEach((event) =>
    window.addEventListener(event, () => {
      svgUnderlineInit(underlineAreas);
    })
  );
};

const extractLinesFromTextNode = (textNode: Text) => {
  if (textNode.nodeType !== 3) {
    return;
  }

  if (!textNode.textContent) {
    return;
  }

  /* Remove any extra white space */
  textNode.textContent = collapseWhiteSpace(textNode.textContent);

  /* Set consts */
  const textContent = textNode.textContent;

  const range = document.createRange();
  let lines: string[][] | (string | unknown)[] = [];
  let lineCharacters: string[] = [];

  /* Iterate over every character in the text node. */
  for (let i = 0; i < textContent.length; i++) {
    /* Set the range to span from the beginning of the text node up to and including the current character */
    range.setStart(textNode, 0);
    range.setEnd(textNode, i + 1);

    /* At this point, the Range's client rectangles will include a rectangle
    for each visually-rendered line of text. Which means, the last
    character in our Range (the current character in our for-loop) will be
    the last character in the last line of text (in our Range). As such, we
    can use the current rectangle count to determine the line of text. */
    const lineIndex = range.getClientRects().length - 1;

    /* If this is the first character in this line, create a new buffer for this line. */
    if (!lines[lineIndex]) {
      lines.push((lineCharacters = []));
    }

    /* Add this character to the currently pending line of text. */
    lineCharacters.push(textContent!.charAt(i));
  }

  /* At this point, we have an array (lines) of arrays (characters). 
  Collapse the character buffers down into a single text value.
  Wrap each line in a span so that each line gets underlined */
  lines = lines.map((characters) => {
    return `<span class="svg-underline">${collapseWhiteSpace(
      characters.join("")
    )}</span>`;
  });

  return lines;
};

/* Helper function to remove and normalise white space between all browsers */
const collapseWhiteSpace = (value: string) => {
  return value.trim().replace(/\s+/g, " ");
};
